// The Ohio State University (Columbus campus): public college page (#2652),
// official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Ohio State's Common Data Set 2025-2026 for the Columbus campus
//    (CDS-2025-2026-OSU-Columbus-Campus.pdf, Office of Institutional Research
//    and Planning). The PDF was printed to PDF, so its numbers carry no text
//    layer; they were read from the rendered pages (C1 page 13, residency
//    table page 14, C8 page 17, C21/C22 page 23). C1 applicants/admits/
//    enrollees for Fall 2025; C8 test policy for Fall 2027. Its G1 marks the
//    2026-2027 costs as not yet final, so the cost table comes from Student
//    Financial Aid's 2026-2027 cost of attendance instead.
//  - undergrad.osu.edu: First-year applicants (Columbus campus), Step-by-step
//    (testing and the Spring/Autumn 2027 deadlines, dated on the page), and
//    Enrollment criteria (entry to majors by college).
//  - sfa.osu.edu: Cost of Attendance 2026-2027, Columbus campus, incoming
//    first-years living on campus, Ohio resident and non-Ohio resident.
//  - College Scorecard, school id 204796 (net price by family income). The
//    DEMO_KEY API was over its rate limit on 2026-10-01, so the figures were
//    read from the Scorecard's own school page, which shows the same latest
//    year the API serves.
// The CDS lists an early action notification of January 31; the admissions
// page (December 11 or January 22) is used for the decision dates.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Ohio State Common Data Set 2025-2026 (Columbus campus)',
        url: 'https://irp.osu.edu/sites/default/files/documents/2026/03/CDS-2025-2026-OSU-Columbus-Campus.pdf',
        read: '2026-10-01',
    },
    steps: {
        label: 'Ohio State Admissions, First-Year Applicants (Columbus), Step-by-step',
        url: 'https://undergrad.osu.edu/apply/freshmen-columbus/apply-step-by-step',
        read: '2026-10-01',
    },
    enrollment: {
        label: 'Ohio State Admissions, Enrollment Criteria for First-Year Students',
        url: 'https://undergrad.osu.edu/apply/freshmen-columbus/who-gets-in/enrollment-criteria',
        read: '2026-10-01',
    },
    costs: {
        label: 'Ohio State Student Financial Aid, Cost of Attendance 2026-2027',
        url: 'https://sfa.osu.edu/incoming-freshmen/about-aid/financial-aid-eligibility',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?204796-Ohio-State-University-Main-Campus',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'ohio-state-university',
    kbId: 'ohio_state_university',
    name: 'The Ohio State University',
    shortName: 'Ohio State',
    aliases: ['OSU', 'tOSU'],
    location: 'Columbus, OH',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Ohio State brand colors); decoration only.
    branding: {
        primary: '#BA0C2F',
        secondary: '#A7B1B7',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/ohio_state_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 81392,
        admitted: 40047,
        enrolled: 8248,
        source: 'cds',
        where: 'section C1, Columbus campus',
        residency: [
            { group: 'In-state', applied: 25273, admitted: 11395 },
            { group: 'Out-of-state', applied: 42898, admitted: 19914 },
            { group: 'International', applied: 13221, admitted: 8738 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Ohio State\'s Common Data Set reports one first-year acceptance rate for the Columbus campus, with no rate by college or major. Once you are admissible, you are considered for your intended major, and some majors start you as a pre-major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Ohio State has no early decision plan, and the Common Data Set does not report how many applied or were admitted in early action, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Once you are admissible to Ohio State, you are reviewed for your intended major. Some colleges ask for more:',
        rows: [
            { school: 'College of Engineering', requirement: 'Admission to engineering majors is competitive. For aerospace, biomedical or mechanical engineering, indicate your interest on the Common Application. Apply by early action to be considered for engineering majors.' },
            { school: 'College of Nursing', requirement: 'Selective direct enrollment as a pre-nursing major (ACT 25 or SAT 1200 and the top 10% of your class). Direct admission to the BSN program is considered only for applicants who meet the early action deadline.' },
            { school: 'Dance and music', requirement: 'You are enrolled in the pre-major only after a successful audition. Audition programs are highly competitive.' },
            { school: 'Actuarial science, computer and information science, data analytics, international studies, neuroscience', requirement: 'Admitted students start as pre-majors, and admission to the major is competitive.' },
        ],
        note: 'A pre-major does not guarantee a place in the major.',
        source: 'enrollment',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'December 11 for Ohio residents, January 22 for others', binding: false },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'March 5', binding: false },
        ],
        note: 'Your complete application, with test scores and transcript, is due by 11:59 pm ET on the deadline. Apply by early action to be considered for university merit scholarships, honors and scholars programs, engineering majors and nursing direct admission. The acceptance fee is due May 1.',
        source: 'steps',
        binding: 'Early action at Ohio State is nonbinding: if you are admitted, you do not have to attend.',
        bindingSource: 'steps',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'Ohio State requires ACT or SAT scores from first-year applicants to the Columbus campus, sent directly from the testing agency by your application deadline. Ohio State superscores: it uses your highest section scores across attempts. Applicants who will have been out of high school for two or more years do not need to send scores.',
        source: 'steps',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as required to be considered for admission for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'steps',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [14034, 14034] },
            { item: 'Non-resident fees', amounts: [0, 30220] },
            { item: 'Housing and food (on campus)', amounts: [15630, 15630] },
            { item: 'Books, supplies and equipment', amounts: [1020, 1020] },
            { item: 'Miscellaneous personal expenses', amounts: [2686, 2686] },
            { item: 'Transportation', amounts: [732, 1360] },
            { item: 'Federal student loan fees', amounts: [40, 40] },
        ],
        totals: [34142, 64990],
        note: 'For first-years on the Columbus campus living on campus. Ohio residents pay no non-resident fees. Costs may vary by program, hours enrolled and housing and dining choices.',
        source: 'costs',
        netPrice: {
            average: 17269,
            byIncome: [
                { income: '$0 to $30,000', amount: 6061 },
                { income: '$30,001 to $48,000', amount: 5538 },
                { income: '$48,001 to $75,000', amount: 9708 },
                { income: '$75,001 to $110,000', amount: 18203 },
                { income: 'Over $110,000', amount: 27873 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Ohio State\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
